import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { auth } from '../firebase';
import './Profile.css';

function Profile() {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const [editingCourse, setEditingCourse] = useState(null);
  const [updatingCourse, setUpdatingCourse] = useState(false);
  const [removingCourse, setRemovingCourse] = useState(false);

  /* 🔹 EXISTING STATE */
  const [allCourses, setAllCourses] = useState([]);
  const [updatingCoursesDoing, setUpdatingCoursesDoing] = useState(false);

  /* 🔹 NEW UI STATE (EXPAND / COLLAPSE) */
  const [showCoursesTutoring, setShowCoursesTutoring] = useState(false);
  const [showCoursesDoing, setShowCoursesDoing] = useState(false);
  const [showAddCourses, setShowAddCourses] = useState(false);

  /* 🔹 SEARCH STATE FOR ADD COURSES */
  const [addCoursesSearchTerm, setAddCoursesSearchTerm] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      const user = auth.currentUser;
      if (!user) return;

      try {
        const token = await user.getIdToken();

        const [userRes, coursesRes] = await Promise.all([
          axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/user/${user.uid}/data`,
            { headers: { Authorization: `Bearer ${token}` } }
          ),
          axios.get(`${process.env.REACT_APP_API_BASE_URL}/courses`)
        ]);

        setUserData(userRes.data);
        setAllCourses(coursesRes.data);

        if (userRes.data.profilePicUrl) {
          setPreviewUrl(userRes.data.profilePicUrl);
        }
      } catch (err) {
        console.error('Error loading profile:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  /* ===================== PROFILE PIC ===================== */

  const onFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setUploadError(null);
  };

  const uploadProfilePicture = async () => {
    if (!selectedFile) return;

    const user = auth.currentUser;
    if (!user) return;

    setUploading(true);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.append('profilePic', selectedFile);

      const token = await user.getIdToken();
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/user/${user.uid}/profile-pic`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setUserData(prev => ({ ...prev, profilePicUrl: res.data.profilePicUrl }));
      setSelectedFile(null);
      alert('Profile picture updated!');
    } catch (err) {
      console.error(err);
      setUploadError('Upload failed, please try again.');
    } finally {
      setUploading(false);
    }
  };

  /* ===================== COURSES TUTORING ===================== */

  const updateCoursePrices = async (courseId, onlinePrice, faceToFacePrice) => {
    const user = auth.currentUser;
    if (!user) return;

    setUpdatingCourse(true);

    try {
      const token = await user.getIdToken();

      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/user/${user.uid}/courses/${courseId}`,
        { onlinePrice, faceToFacePrice },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update local state
      setUserData(prev => ({
        ...prev,
        coursesTutoring: prev.coursesTutoring.map(course => 
          course.courseId === courseId 
            ? { ...course, onlinePrice, faceToFacePrice }
            : course
        )
      }));

      setEditingCourse(null);
      alert('Course prices updated successfully!');
    } catch (err) {
      console.error(err);
      alert('Failed to update course prices');
    } finally {
      setUpdatingCourse(false);
    }
  };

  const removeCourseTutoring = async (courseId) => {
    const user = auth.currentUser;
    if (!user) return;

    setRemovingCourse(true);

    try {
      const token = await user.getIdToken();

      await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/user/${user.uid}/courses/${courseId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setUserData(prev => ({
        ...prev,
        coursesTutoring: prev.coursesTutoring.filter(c => c.courseId !== courseId)
      }));

      alert('Course removed from tutoring list successfully!');
    } catch (err) {
      console.error(err);
      alert('Failed to remove course');
    } finally {
      setRemovingCourse(false);
    }
  };

  /* ===================== COURSES DOING ===================== */

  const coursesDoingIds = userData?.coursesDoing?.map(c => c.courseId) || [];

  const availableCourses = allCourses.filter(
    c => !coursesDoingIds.includes(c.id)
  );

  /* ===================== FILTER AVAILABLE COURSES BASED ON SEARCH ===================== */
  const filteredAvailableCourses = availableCourses.filter(course => 
    course.name.toLowerCase().includes(addCoursesSearchTerm.toLowerCase()) ||
    course.id.toLowerCase().includes(addCoursesSearchTerm.toLowerCase()) ||
    (course.description && course.description.toLowerCase().includes(addCoursesSearchTerm.toLowerCase()))
  );

  const addCourseDoing = async (courseId) => {
    const user = auth.currentUser;
    if (!user) return;

    setUpdatingCoursesDoing(true);

    try {
      const token = await user.getIdToken();

      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/user/${user.uid}/courses-doing`,
        { courseId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const addedCourse = allCourses.find(c => c.id === courseId);

      setUserData(prev => ({
        ...prev,
        coursesDoing: [...(prev.coursesDoing || []), {
          courseId: addedCourse.id,
          name: addedCourse.name,
          description: addedCourse.description || ''
        }]
      }));

      // Clear search after adding
      setAddCoursesSearchTerm('');
    } catch (err) {
      console.error(err);
      alert('Failed to add course');
    } finally {
      setUpdatingCoursesDoing(false);
    }
  };

  const removeCourseDoing = async (courseId) => {
    const user = auth.currentUser;
    if (!user) return;

    setUpdatingCoursesDoing(true);

    try {
      const token = await user.getIdToken();

      await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/user/${user.uid}/courses-doing/${courseId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setUserData(prev => ({
        ...prev,
        coursesDoing: prev.coursesDoing.filter(c => c.courseId !== courseId)
      }));
    } catch (err) {
      console.error(err);
      alert('Failed to remove course');
    } finally {
      setUpdatingCoursesDoing(false);
    }
  };

  /* ===================== RENDER ===================== */

  if (loading) return <div className="profile-loading">Loading profile...</div>;
  if (!userData) return <div className="profile-error">No user data found.</div>;

  return (
    <div className="profile-container">
      <h2 className="profile-title">Your Profile</h2>

      {/* Profile Header Card */}
      <div className="profile-header-card">
        <div className="profile-pic-wrapper">
          <img
            src={previewUrl || '/default-profile.png'}
            alt="Profile"
            className="profile-pic"
          />
          <div className="profile-status">
            <span className={`status-dot ${userData.availability ? 'available' : 'unavailable'}`}></span>
            <span className="status-text">{userData.availability ? 'Available' : 'Unavailable'}</span>
          </div>
        </div>
        
        <div className="profile-info">
          <div className="profile-field">
            <span className="field-label">Username</span>
            <span className="field-value">{userData.username}</span>
          </div>
          <div className="profile-field">
            <span className="field-label">Email</span>
            <span className="field-value">{userData.email}</span>
          </div>
          <div className="profile-field">
            <span className="field-label">Points</span>
            <span className="points-value">{userData.points} points</span>
          </div>
        </div>
      </div>

      {/* Profile Picture Upload */}
      <div className="upload-section">
        <div className="upload-header">
          <h3 className="upload-title">Update Profile Picture</h3>
          <div className="file-input-wrapper">
            <input 
              type="file" 
              accept="image/*" 
              onChange={onFileChange} 
              id="profile-upload"
              className="file-input"
            />
            <label htmlFor="profile-upload" className="file-input-label">
              Choose File
            </label>
            <span className="file-name">
              {selectedFile ? selectedFile.name : 'No file chosen'}
            </span>
          </div>
        </div>
        
        {uploadError && <p className="upload-error">{uploadError}</p>}
        
        <div className="upload-actions">
          <button 
            onClick={uploadProfilePicture} 
            disabled={uploading || !selectedFile}
            className="upload-button"
          >
            {uploading ? (
              <span className="uploading-text">
                <span className="spinner"></span>
                Uploading...
              </span>
            ) : 'Upload Picture'}
          </button>
        </div>
      </div>

      {/* ===================== COURSES SECTION ===================== */}
      <div className="courses-section">

        {/* COURSES TUTORING - NEW SECTION */}
        <div className="courses-block">
          <button
            className="expand-toggle"
            onClick={() => setShowCoursesTutoring(prev => !prev)}
          >
            <span className="toggle-icon">{showCoursesTutoring ? '▼' : '▶'}</span>
            <span className="toggle-text">Courses You Are Tutoring</span>
            <span className="toggle-count">({userData.coursesTutoring?.length || 0})</span>
          </button>

          {showCoursesTutoring && (
            <div className="expandable-content">
              {userData.coursesTutoring?.length > 0 ? (
                <div className="courses-grid">
                  {userData.coursesTutoring.map(course => (
                    <div key={course.courseId} className="course-card tutoring-card">
                      <div className="course-info">
                        <h4 className="course-name">{course.courseName}</h4>
                        <p className="course-id">ID: {course.courseId}</p>
                        
                        {editingCourse === course.courseId ? (
                          <div className="price-edit-form">
                            <div className="price-input-group">
                              <label>Online Price ($):</label>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                defaultValue={course.onlinePrice}
                                id={`online-${course.courseId}`}
                                className="price-input"
                              />
                            </div>
                            <div className="price-input-group">
                              <label>Face to Face Price ($):</label>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                defaultValue={course.faceToFacePrice}
                                id={`f2f-${course.courseId}`}
                                className="price-input"
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="course-prices">
                            <p className="price online">
                              <span className="price-label">Online:</span> 
                              R{course.onlinePrice?.toFixed(2) || '0.00'}
                            </p>
                            <p className="price face-to-face">
                              <span className="price-label">Face to Face:</span> 
                              R{course.faceToFacePrice?.toFixed(2) || '0.00'}
                            </p>
                          </div>
                        )}
                      </div>
                      
                      <div className="course-actions">
                        {editingCourse === course.courseId ? (
                          <>
                            <button
                              onClick={() => {
                                const onlinePrice = parseFloat(document.getElementById(`online-${course.courseId}`).value);
                                const faceToFacePrice = parseFloat(document.getElementById(`f2f-${course.courseId}`).value);
                                updateCoursePrices(course.courseId, onlinePrice, faceToFacePrice);
                              }}
                              disabled={updatingCourse}
                              className="course-save-btn"
                            >
                              {updatingCourse ? (
                                <span className="button-spinner"></span>
                              ) : 'Save'}
                            </button>
                            <button
                              onClick={() => setEditingCourse(null)}
                              className="course-cancel-btn"
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => setEditingCourse(course.courseId)}
                              className="course-edit-btn"
                            >
                              Edit Prices
                            </button>
                            <button
                              onClick={() => {
                                if (window.confirm('Are you sure you want to remove this course from your tutoring list?')) {
                                  removeCourseTutoring(course.courseId);
                                }
                              }}
                              disabled={removingCourse}
                              className="course-remove-btn"
                            >
                              {removingCourse ? (
                                <span className="button-spinner"></span>
                              ) : 'Remove'}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-courses">You are not tutoring any courses.</p>
              )}
            </div>
          )}
        </div>

        {/* COURSES DOING */}
        <div className="courses-block">
          <button
            className="expand-toggle"
            onClick={() => setShowCoursesDoing(prev => !prev)}
          >
            <span className="toggle-icon">{showCoursesDoing ? '▼' : '▶'}</span>
            <span className="toggle-text">Courses You Are Doing</span>
            <span className="toggle-count">({userData.coursesDoing?.length || 0})</span>
          </button>

          {showCoursesDoing && (
            <div className="expandable-content">
              {userData.coursesDoing?.length > 0 ? (
                <div className="courses-grid">
                  {userData.coursesDoing.map(course => (
                    <div key={course.courseId} className="course-card">
                      <div className="course-info">
                        <h4 className="course-name">{course.name}</h4>
                        <p className="course-id">ID: {course.courseId}</p>
                        {course.description && (
                          <p className="course-description">{course.description}</p>
                        )}
                      </div>
                      <button
                        onClick={() => removeCourseDoing(course.courseId)}
                        disabled={updatingCoursesDoing}
                        className="course-remove-btn"
                      >
                        {updatingCoursesDoing ? (
                          <span className="button-spinner"></span>
                        ) : 'Remove'}
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-courses">You are not enrolled in any courses.</p>
              )}
            </div>
          )}
        </div>

        {/* ADD COURSE */}
        <div className="courses-block">
          <button
            className="expand-toggle"
            onClick={() => setShowAddCourses(prev => !prev)}
          >
            <span className="toggle-icon">{showAddCourses ? '▼' : '▶'}</span>
            <span className="toggle-text">Add a Course</span>
            <span className="toggle-count">({availableCourses.length} available)</span>
          </button>

          {showAddCourses && (
            <div className="expandable-content">
              {/* Search Bar for Add Courses */}
              <div className="search-container">
                <input
                  type="text"
                  placeholder="Search courses by name, ID, or description..."
                  value={addCoursesSearchTerm}
                  onChange={(e) => setAddCoursesSearchTerm(e.target.value)}
                  className="search-input"
                />
                {addCoursesSearchTerm && (
                  <button
                    onClick={() => setAddCoursesSearchTerm('')}
                    className="clear-search-btn"
                  >
                    Clear
                  </button>
                )}
              </div>

              {filteredAvailableCourses.length > 0 ? (
                <>
                  {addCoursesSearchTerm && (
                    <p className="search-results-count">
                      Found {filteredAvailableCourses.length} course(s)
                    </p>
                  )}
                  <div className="courses-grid">
                    {filteredAvailableCourses.map(course => (
                      <div key={course.id} className="course-card add-course">
                        <div className="course-info">
                          <h4 className="course-name">{course.name}</h4>
                          <p className="course-id">ID: {course.id}</p>
                          {course.description && (
                            <p className="course-description">{course.description}</p>
                          )}
                        </div>
                        <button
                          onClick={() => addCourseDoing(course.id)}
                          disabled={updatingCoursesDoing}
                          className="course-add-btn"
                        >
                          {updatingCoursesDoing ? (
                            <span className="button-spinner"></span>
                          ) : 'Add Course'}
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="no-courses">
                  {addCoursesSearchTerm 
                    ? 'No courses match your search.' 
                    : 'No available courses to add.'}
                </p>
              )}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

export default Profile;
